<template>
    <modal :icons="true"
           :value="open"
           @input="$emit('open', false)"
    >
        <div :id="selectUniqueId"
             ref="list"
             role="listbox"
             class="select-options d-flex flex-column justify-content-center align-items-center"
             tabindex="-1"
             :aria-activedescendant="activeDescendant"
             :aria-labelledby="labelId || ''"
             @keydown="handleKeydown($event)"
             @blur="$emit('open', false)"
        >
            <div v-for="(option, index) in options"
                 :id="optionUniqueId(index)"
                 :key="index"
                 role="option"
                 class="select-option secondary-text d-block w-100 py-1"
                 :class="selectClasses(option)"
                 :aria-selected="ariaSelected(option)"
                 @click="select(option)"
            >
                {{ labels[index] }}
            </div>
        </div>
    </modal>
</template>

<script>
    import Vue from 'vue';

    export default Vue.extend({
        props: {
            open: {
                type: Boolean,
                required: true,
            },
            options: {
                type: Array,
                required: true,
            },
            labels: {
                type: Array,
                required: true,
            },
            value: {
                type: Array,
                required: true,
            },
            uniqueId: {
                type: Number,
                required: true,
            },
            labelId: {
                type: String,
                required: true,
            },
        },
        data () {
            return {
                currentValue: this.value,
            };
        },
        computed: {
            activeDescendant () {
                let index = this.options.indexOf(this.currentValue[0]);
                return index === -1 ? '' : this.optionUniqueId(index);
            },
            selectUniqueId () {
                return `select-listbox-${this.uniqueId}`;
            },
        },
        watch: {
            open (curr) {
                if (curr) {
                    this.currentValue = this.value;
                    setTimeout(() => this.$refs.list.focus());
                    if (this.currentValue.length) {
                        setTimeout(() => this.scrollToOption(this.currentValue[0]));
                    }
                }
            },
            value (curr) {
                this.currentValue = curr;
            },
            currentValue (curr) {
                if (curr.length) {
                    setTimeout(() => this.scrollToOption(curr[0]));
                }
            },
        },
        methods: {
            select (option) {
                if (this.currentValue.includes(option)) {
                    this.$emit('select', this.currentValue.filter(o => o !== option));
                } else if (this.isMultiple) {
                    this.$emit('select', [ ...this.currentValue, option ]);
                } else {
                    this.$emit('select', [ option ]);
                }

                if (!this.isMultiple) {
                    this.$emit('open', false);
                }
            },
            selectClasses (option) {
                return {
                    selected: this.currentValue.includes(option),
                };
            },
            isSelected (option) {
                return this.currentValue.includes(option);
            },
            ariaSelected (option) {
                return this.isSelected(option) ? 'true' : 'false';
            },
            optionUniqueId (index) {
                return `${this.selectUniqueId}--option-${index}`;
            },
            scrollToOption (option) {
                const optionIndex = this.options.indexOf(option);
                if (optionIndex === -1) return;

                function scrollableParent (node) {
                    const parent = node.parentNode;
                    if (!parent) return null;
                    return parent.scrollHeight > parent.clientHeight ? parent : scrollableParent(parent);
                }

                const optionEl = document.getElementById(this.optionUniqueId(optionIndex));
                const parentEl = scrollableParent(optionEl);

                const parentTop    = parentEl.scrollTop;
                const parentBottom = parentEl.clientHeight + parentEl.scrollTop;
                const optionTop    = optionEl.offsetTop - parentEl.offsetTop;
                const optionBottom = optionTop + optionEl.offsetHeight;

                const isTopInside    = optionTop > parentTop;
                const isBottomInside = optionBottom < parentBottom;

                if (!isTopInside) {
                    parentEl.scrollTop = optionTop;
                } else if (!isBottomInside) {
                    parentEl.scrollTop = optionBottom - parentEl.clientHeight;
                }
            },
            getOption (index) {
                const len          = this.options.length;
                const wrappedIndex = (len + index) % len;
                return this.options[wrappedIndex];
            },
            handleKeydown (e) {
                const currentOptionIndex = this.options.indexOf(this.currentValue[0]);
                switch (e.key) {
                    case 'ArrowDown':
                        this.currentValue = [ this.getOption(currentOptionIndex + 1) ];
                        e.preventDefault();
                        break;
                    case 'ArrowUp':
                        this.currentValue = [ this.getOption(currentOptionIndex - 1) ];
                        e.preventDefault();
                        break;
                    case 'Enter':
                        this.$emit('select', this.currentValue);
                        this.$emit('open', false);
                        break;
                    case 'Escape':
                        this.$emit('open', false);
                        break;
                    case 'Home':
                        this.currentValue = [ this.getOption(0) ];
                        break;
                    case 'End':
                        this.currentValue = [ this.getOption(this.options.length - 1) ];
                        break;
                }
            },
        },
    });
</script>
